import { supabase } from './supabase';
import { formatRSD } from './helpers';

// ============================================================
// OBAVEŠTENJA — vrste, tekstovi (sr/en/ru) i pravljenje novih
// Obaveštenja se računaju u aplikaciji pri otvaranju (i na 30 min)
// za SVE naloge korisnika; duplikati se sprečavaju ključem dedupe_key.
// ============================================================

const T = (lang, sr, en, ru) => (lang === 'ru' ? ru : lang === 'en' ? en : sr);

export const NOTIFICATION_TYPES = [
  { type: 'tax_deadline', label: { sr: 'Rok za plaćanje poreza i doprinosa', en: 'Tax and contributions deadline', ru: 'Срок уплаты налогов и взносов' } },
  { type: 'income_3m', label: { sr: 'Prihod preko 3 miliona dinara', en: 'Income over 3 million RSD', ru: 'Доход свыше 3 млн динаров' } },
  { type: 'income_5m', label: { sr: 'Prihod preko 5 miliona dinara', en: 'Income over 5 million RSD', ru: 'Доход свыше 5 млн динаров' } },
  { type: 'income_6m', label: { sr: 'Prihod preko 6 miliona dinara (limit paušala)', en: 'Income over 6 million RSD (flat-rate limit)', ru: 'Доход свыше 6 млн динаров (лимит паушала)' } },
  { type: 'income_7m', label: { sr: 'Prihod preko 7 miliona dinara', en: 'Income over 7 million RSD', ru: 'Доход свыше 7 млн динаров' } },
  { type: 'income_8m', label: { sr: 'Prihod preko 8 miliona dinara (granica PDV-a)', en: 'Income over 8 million RSD (VAT threshold)', ru: 'Доход свыше 8 млн динаров (порог НДС)' } },
  { type: 'invoice_overdue', label: { sr: 'Faktura nije plaćena do datuma valute', en: 'Invoice unpaid after due date', ru: 'Счёт не оплачен в срок' } },
  { type: 'app_news', label: { sr: 'Nove funkcionalnosti aplikacije', en: 'New app features', ru: 'Новые функции приложения' } },
];

// Novosti u aplikaciji (dodaj novu stavku kad izađe nešto novo)
export const APP_NEWS = [
  { id: '2026-10-ruski', date: '2026-10-15', link: '/profil',
    title: { sr: 'Novo: aplikacija na ruskom jeziku', en: 'New: the app in Russian', ru: 'Новое: приложение на русском языке' },
    body: { sr: 'Jezik možeš promeniti u Profilu korisnika.', en: 'Change the language in your User profile.', ru: 'Язык можно сменить в профиле пользователя.' } },
  { id: '2026-10-obavestenja', date: '2026-10-15', link: '/podesavanja/notifikacije',
    title: { sr: 'Novo: obaveštenja', en: 'New: notifications', ru: 'Новое: уведомления' },
    body: { sr: 'Podsetnici za porez, limite prihoda i neplaćene fakture. Podesi ih u Podešavanjima.', en: 'Reminders for taxes, income limits and unpaid invoices. Adjust them in Settings.', ru: 'Напоминания о налогах, лимитах дохода и неоплаченных счетах. Настройте их в разделе «Настройки».' } },
];

const MILLION = 1000000;
const THRESHOLDS = [3, 5, 6, 7]; // kalendarska godina
const pad = (n) => String(n).padStart(2, '0');
const isoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const dmy = (iso) => (iso ? iso.split('-').reverse().join('.') + '.' : '');
const toRsd = (inv) => (Number(inv.amount) || 0) * (inv.currency && inv.currency !== 'RSD' ? (Number(inv.exchange_rate) || 0) : 1);

// Tekst obaveštenja na jeziku korisnika (čuvaju se samo podaci, tekst se sastavlja pri prikazu)
export function describe(n, lang) {
  const d = n.data || {};
  const who = d.agencyName ? `${d.agencyName}: ` : '';
  switch (n.type) {
    case 'tax_deadline':
      return d.overdue
        ? { title: who + T(lang, `porez i doprinosi za ${d.period} nisu označeni kao plaćeni`, `tax for ${d.period} not marked as paid`, `налог за ${d.period} не отмечен как оплаченный`),
            body: T(lang, `Rok je bio ${dmy(d.deadline)}. Ako je plaćeno, označi uplatu na Početnoj.`, `The deadline was ${dmy(d.deadline)}. If paid, mark it on the Dashboard.`, `Срок истёк ${dmy(d.deadline)}. Если оплачено, отметьте это на главной.`) }
        : { title: who + T(lang, `rok za porez i doprinose za ${d.period}`, `tax deadline for ${d.period}`, `срок уплаты налога за ${d.period}`),
            body: T(lang, `Platiti do ${dmy(d.deadline)}.`, `Pay by ${dmy(d.deadline)}.`, `Оплатить до ${dmy(d.deadline)}.`) };
    case 'income_3m': case 'income_5m': case 'income_6m': case 'income_7m': {
      const m = n.type.replace(/\D/g, '');
      return {
        title: who + T(lang, `prihod u ${d.year}. prešao ${m} miliona dinara`, `${d.year} income passed ${m} million RSD`, `доход за ${d.year} превысил ${m} млн динаров`),
        body: (m === '6'
          ? T(lang, 'Prekoračen je godišnji limit od 6 miliona za paušalno oporezivanje. ', 'The annual 6 million flat-rate limit is exceeded. ', 'Превышен годовой лимит паушала в 6 млн. ')
          : '') + T(lang, `Ukupno fakturisano: ${d.total}.`, `Total invoiced: ${d.total}.`, `Всего выставлено: ${d.total}.`),
      };
    }
    case 'income_8m':
      return { title: who + T(lang, 'prihod u poslednjih 365 dana prešao 8 miliona', 'income in the last 365 days passed 8 million', 'доход за последние 365 дней превысил 8 млн'),
        body: T(lang, `To je granica za ulazak u sistem PDV-a. Ukupno: ${d.total}.`, `This is the VAT registration threshold. Total: ${d.total}.`, `Это порог регистрации плательщиком НДС. Всего: ${d.total}.`) };
    case 'invoice_overdue':
      return { title: who + T(lang, `faktura ${d.number} nije plaćena`, `invoice ${d.number} is unpaid`, `счёт ${d.number} не оплачен`),
        body: T(lang, `${d.client || ''} · ${d.amount} · valuta ${dmy(d.due)}`, `${d.client || ''} · ${d.amount} · due ${dmy(d.due)}`, `${d.client || ''} · ${d.amount} · срок ${dmy(d.due)}`) };
    case 'app_news': {
      const item = APP_NEWS.find((x) => x.id === d.newsId);
      return item ? { title: item.title[lang] || item.title.sr, body: item.body[lang] || item.body.sr } : { title: '', body: '' };
    }
    default:
      return { title: n.type, body: '' };
  }
}

// Podešavanja korisnika (podrazumevano: u aplikaciji uključeno, e-mail isključen)
export async function loadSettings() {
  const { data } = await supabase.from('notification_settings').select('*');
  const map = {};
  NOTIFICATION_TYPES.forEach((t) => { map[t.type] = { in_app: true, email: false }; });
  (data || []).forEach((r) => { map[r.type] = { in_app: r.in_app, email: r.email }; });
  return map;
}

// Napravi nova obaveštenja za sve naloge korisnika
export async function generateNotifications(user, agencies) {
  if (!user || !agencies?.length) return;
  const settings = await loadSettings();
  const on = (type) => settings[type]?.in_app !== false;
  const ids = agencies.map((a) => a.id);
  const nameOf = Object.fromEntries(agencies.map((a) => [a.id, a.name]));
  const today = new Date();
  const todayIso = isoDate(today);
  const year = today.getFullYear();
  const from365 = new Date(today); from365.setDate(from365.getDate() - 365);
  const fromIso = isoDate(new Date(Math.min(from365.getTime(), new Date(year, 0, 1).getTime())));
  const rows = [];
  const add = (type, agencyId, dedupe, data, link) => {
    if (!on(type)) return;
    rows.push({ user_id: user.id, agency_id: agencyId || null, type, data, link, dedupe_key: dedupe });
  };

  const { data: invoices } = await supabase.from('invoices')
    .select('id, agency_id, invoice_number, client, amount, currency, exchange_rate, issue_date, due_date, status, document_kind')
    .in('agency_id', ids).gte('issue_date', fromIso);

  // Prihod po nalogu
  const counted = (invoices || []).filter((i) => i.document_kind !== 'profaktura' && i.status !== 'stornirano');
  ids.forEach((aid) => {
    const mine = counted.filter((i) => i.agency_id === aid);
    const yearTotal = mine.filter((i) => String(i.issue_date).startsWith(String(year))).reduce((s, i) => s + toRsd(i), 0);
    THRESHOLDS.forEach((m) => {
      if (yearTotal > m * MILLION) {
        add(`income_${m}m`, aid, `income_${m}m:${aid}:${year}`, { agencyName: nameOf[aid], year, total: formatRSD(yearTotal) }, '/');
      }
    });
    const total365 = mine.filter((i) => i.issue_date >= isoDate(from365)).reduce((s, i) => s + toRsd(i), 0);
    if (total365 > 8 * MILLION) {
      add('income_8m', aid, `income_8m:${aid}:${year}`, { agencyName: nameOf[aid], total: formatRSD(total365) }, '/');
    }
  });

  // Neplaćene fakture posle datuma valute
  (invoices || []).forEach((i) => {
    if (i.document_kind === 'faktura' && i.due_date && i.due_date < todayIso && (i.status === 'poslato' || i.status === 'delimicno')) {
      const amount = i.currency && i.currency !== 'RSD'
        ? `${(Number(i.amount) || 0).toLocaleString('sr-RS', { minimumFractionDigits: 2 })} ${i.currency}`
        : formatRSD(Number(i.amount) || 0);
      add('invoice_overdue', i.agency_id, `overdue:${i.id}`,
        { agencyName: nameOf[i.agency_id], number: i.invoice_number, client: i.client, amount, due: i.due_date },
        `/fakturisanje/${i.id}/pregled`);
    }
  });

  // Porez i doprinosi: za prethodni mesec, rok 15. u tekućem; podsetnik od 8. u mesecu
  const day = today.getDate();
  if (day >= 8) {
    const prev = new Date(year, today.getMonth() - 1, 1);
    const py = prev.getFullYear();
    const pm = prev.getMonth() + 1;
    const { data: paidRows } = await supabase.from('tax_payments')
      .select('agency_id, paid').in('agency_id', ids).eq('period_year', py).eq('period_month', pm);
    const paid = new Set((paidRows || []).filter((r) => r.paid).map((r) => r.agency_id));
    const deadline = `${year}-${pad(today.getMonth() + 1)}-15`;
    const overdue = day > 15;
    ids.forEach((aid) => {
      if (paid.has(aid)) return;
      add('tax_deadline', aid, `tax:${aid}:${py}-${pad(pm)}:${overdue ? 'late' : 'soon'}`,
        { agencyName: nameOf[aid], period: `${pad(pm)}/${py}`, deadline, overdue }, '/');
    });
  }

  // Novosti
  APP_NEWS.forEach((n) => add('app_news', null, `news:${n.id}`, { newsId: n.id }, n.link));

  if (rows.length) {
    await supabase.from('notifications').upsert(rows, { onConflict: 'user_id,dedupe_key', ignoreDuplicates: true });
  }
}
